import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TopicCard from '@/components/feature/TopicCard';
import { usePageSeo } from '@/hooks/usePageSeo';
import { categories } from '@/mocks/categories';
import { topics, statusMeta, type TopicStatus } from '@/mocks/topics';

const statusOrder: TopicStatus[] = ['verified', 'debunked', 'pending', 'doubtful'];

const listStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '求证专区｜瓜田求证社',
  url: 'https://xinnanwangluo.cn/topics',
  inLanguage: 'zh-CN',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xinnanwangluo.cn/' },
      { '@type': 'ListItem', position: 2, name: '求证专区', item: 'https://xinnanwangluo.cn/topics' },
    ],
  },
};

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') ?? 'all';
  const status = (searchParams.get('status') ?? 'all') as TopicStatus | 'all';
  const q = searchParams.get('q') ?? '';
  const sort = searchParams.get('sort') ?? 'heat';
  const [keyword, setKeyword] = useState(q);

  usePageSeo({
    title: '求证专区 | 网络爆料真假求证与辟谣话题列表 - 瓜田求证社',
    description:
      '浏览全部网络爆料求证话题：娱乐瓜田、商业传闻、科技爆料、社会热点、生活消费与体坛风云。支持按分类、求证状态筛选，查看每条传言的来源清单与结论。',
    keywords: '网络爆料列表,爆料求证,辟谣话题,网络爆料真假,热门爆料讨论',
    path: '/topics',
    structuredData: listStructuredData,
  });

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next, { replace: true });
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParams({ q: keyword.trim() });
  };

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    const result = topics.filter((topic) => {
      const matchCategory = category === 'all' || topic.categoryId === category;
      const matchStatus = status === 'all' || topic.status === status;
      const matchKeyword =
        !kw ||
        topic.title.toLowerCase().includes(kw) ||
        topic.summary.toLowerCase().includes(kw) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchStatus && matchKeyword;
    });
    return result.sort((a, b) => {
      if (sort === 'date') return a.date < b.date ? 1 : -1;
      return b.heat - a.heat;
    });
  }, [category, status, q, sort]);

  const activeCategory = categories.find((item) => item.id === category);

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: '求证专区' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">求证专区</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            这里收录了我们正在追踪和已经完成的求证话题。每条记录都包含传言原文、来源清单、时间线与结论，
            你可以顺着证据自己判断，而不是只接受一个结论。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-8 md:py-12">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <div className="rounded-xl border border-background-200 bg-background-100/50 p-4 md:p-5">
            <form onSubmit={handleSearch} className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索话题标题、关键词或标签…"
                  aria-label="搜索求证话题"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800"
              >
                <i className="ri-search-eye-line"></i>
                搜索
              </button>
            </form>

            <div className="mt-5 flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-label text-xs text-foreground-500">分类</span>
                <button
                  type="button"
                  onClick={() => updateParams({ category: 'all' })}
                  className={`whitespace-nowrap rounded-full border px-3 py-1 font-label text-xs transition-colors ${
                    category === 'all'
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                  }`}
                >
                  全部
                </button>
                {categories.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => updateParams({ category: item.id })}
                    className={`whitespace-nowrap rounded-full border px-3 py-1 font-label text-xs transition-colors ${
                      category === item.id
                        ? 'border-primary-500 bg-primary-500 text-background-50'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="font-label text-xs text-foreground-500">状态</span>
                <button
                  type="button"
                  onClick={() => updateParams({ status: 'all' })}
                  className={`whitespace-nowrap rounded-full border px-3 py-1 font-label text-xs transition-colors ${
                    status === 'all'
                      ? 'border-secondary-500 bg-secondary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300'
                  }`}
                >
                  全部
                </button>
                {statusOrder.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => updateParams({ status: key })}
                    className={`whitespace-nowrap rounded-full border px-3 py-1 font-label text-xs transition-colors ${
                      status === key
                        ? 'border-secondary-500 bg-secondary-500 text-background-50'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300'
                    }`}
                  >
                    {statusMeta[key].label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-label text-sm text-foreground-600">
              共找到 <span className="font-semibold text-foreground-950">{filtered.length}</span> 条
              {activeCategory ? ` · ${activeCategory.name}` : ''}
              {q ? ` · 关键词「${q}」` : ''}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => updateParams({ sort: 'heat' })}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 font-label text-xs transition-colors ${
                  sort === 'heat' ? 'bg-primary-100 text-primary-700' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                按热度
              </button>
              <button
                type="button"
                onClick={() => updateParams({ sort: 'date' })}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 font-label text-xs transition-colors ${
                  sort === 'date' ? 'bg-primary-100 text-primary-700' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                按时间
              </button>
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center rounded-xl border border-dashed border-background-300 bg-background-100/40 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-base font-semibold text-foreground-900">没有匹配的求证话题</p>
              <p className="mt-2 text-sm text-foreground-600">换个关键词，或者把分类和状态重置后再试试。</p>
              <button
                type="button"
                onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}